import {classes, unique_id} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {Icon} from '@bitstillery/common/components/ui/icon/icon'
import {is_signal} from '@bitstillery/common/lib/store'
import {unchecked} from '@bitstillery/common/lib/unchecked'
import AirDatepicker from 'air-datepicker'
import localeEn from 'air-datepicker/locale/en'
import {DateTime} from 'luxon'
import {watch} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'

import type {AirDatepickerLocale, AirDatepickerOptions} from 'air-datepicker'
import {Signal} from '@bitstillery/mithril'
import type {Unwatch} from '@bitstillery/mithril'
import {failure_message} from '@bitstillery/common/lib/validation'
import type {Validator} from '@bitstillery/common/lib/validation'

interface DateModel {
    date: string | null
    to_date?: string | null | undefined
}

type DateModelRef = DateModel | Signal<string | null> | {value: string | null}

/** air-datepicker's options, re-exported so packages that only use FieldDate needn't depend on it. */
export type DatePickerOptions = AirDatepickerOptions

interface DatePickerAttrs {
    className?: string
    date_picker_options?: AirDatepickerOptions
    disabled?: boolean | undefined
    help?: string
    icon?: string
    label?: string
    model?: DateModelRef
    placeholder?: string
    preset?: 'months' | 'years' | 'days'
    timepicker?: boolean
    validation?: Validator | undefined
}

function is_date_model(obj: unknown): obj is DateModel {
    return typeof obj === 'object' && obj !== null && 'date' in obj
}

export class FieldDate extends MithrilComponent<DatePickerAttrs> {
    preset: NonNullable<DatePickerAttrs['preset']> = 'days'
    signal?: Signal<string | null> | {value: string | null}
    date_model?: DateModel
    onselect_from_airdate_picker = false
    watchers: Unwatch[] = []

    id: string = unique_id(8, false)

    override oncreate(vnode: m.Vnode<DatePickerAttrs>) {
        const {model} = vnode.attrs
        this.preset = vnode.attrs.preset ?? this.preset

        let start_date = new Date().toISOString()
        if (model) {
            if (is_signal(model)) {
                this.signal = model
                if (model.value) {
                    start_date = model.value
                }
            } else if (is_date_model(model)) {
                this.date_model = model
                if (model.date) {
                    start_date = model.date
                }
            }
        }

        // The bundler hands over the CommonJS module object, not the `default` export its declaration promises.
        const locale = unchecked<{default?: AirDatepickerLocale}>(localeEn).default
        const default_date_picker_options: AirDatepickerOptions = {
            autoClose: true,
            dateFormat: (() => {
                switch (this.preset) {
                    case 'years': {
                        return 'yyyy'
                    }
                    case 'months': {
                        return 'MMMM yyyy'
                    }
                    case 'days':
                    default: {
                        return 'dd-MM-yyyy'
                    }
                }
            })(),
            ...(locale === undefined ? {} : {locale}),
            minView: this.preset,
            multipleDatesSeparator: ' - ',
            onSelect: ({date}: {date: Date | Date[]}) => {
                if (vnode.attrs.validation) {
                    vnode.attrs.validation.dirty = true
                }

                // Clearing a range selection hands over an empty array, which leaves the date empty.
                const first_date = Array.isArray(date) ? date[0] : undefined
                const newValue = Array.isArray(date)
                    ? first_date
                        ? DateTime.fromJSDate(first_date).toISODate()
                        : null
                    : vnode.attrs.timepicker
                      ? DateTime.fromJSDate(date).toFormat('yyyy-MM-dd HH:mm a').toLowerCase()
                      : DateTime.fromJSDate(date).toISODate()
                const newToDate = Array.isArray(date) && date[1] ? (DateTime.fromJSDate(date[1]).toISODate() ?? null) : undefined

                if (this.signal) {
                    this.onselect_from_airdate_picker = true
                    this.signal.value = newValue
                } else if (this.date_model) {
                    if (Array.isArray(date)) {
                        this.date_model.date = first_date ? DateTime.fromJSDate(first_date).toISODate() : null
                        this.date_model.to_date = newToDate ?? undefined
                    } else {
                        if (vnode.attrs.timepicker) {
                            this.date_model.date = DateTime.fromJSDate(date).toFormat('yyyy-MM-dd HH:mm a').toLowerCase()
                        } else {
                            this.date_model.date = DateTime.fromJSDate(date).toISODate()
                        }
                    }
                }
            },
            startDate: start_date || new Date().toISOString(),
            timeFormat: 'HH:mm',
            view: this.preset,
        }
        if (vnode.attrs.timepicker) {
            default_date_picker_options.timepicker = true
        }

        const ad = new AirDatepicker(`#${this.id}`, {
            ...default_date_picker_options,
            ...vnode.attrs.date_picker_options,
        })

        // Set the given date(s) in the model to selected
        if (this.signal?.value && Date.parse(this.signal.value)) {
            void ad.selectDate(this.signal.value)
        }
        if (this.date_model?.date && Date.parse(this.date_model.date)) {
            void ad.selectDate(this.date_model.date)
        }
        if (vnode.attrs.date_picker_options?.range && this.date_model?.to_date && Date.parse(this.date_model.to_date)) {
            void ad.selectDate(this.date_model.to_date)
        }

        // Only a state signal can be watched; a plain `{value}` model can't.
        if (this.signal instanceof Signal) {
            this.watchers.push(
                watch(this.signal, (new_value) => {
                    if (this.onselect_from_airdate_picker) {
                        this.onselect_from_airdate_picker = false
                        return
                    }
                    if (new_value && Date.parse(new_value)) {
                        void ad.selectDate(new_value)
                    }
                }),
            )
        }
    }

    override onremove() {
        this.watchers.map((unwatch) => unwatch())
    }

    view(vnode: m.Vnode<DatePickerAttrs>) {
        const {validation} = vnode.attrs
        const invalid = validation ? validation._invalid : false

        return (
            <div
                className={classes('c-field-date', 'field', vnode.attrs.className, vnode.attrs.preset, {
                    disabled: vnode.attrs.disabled,
                    invalid: invalid && validation?.dirty,
                    valid: validation && !invalid && validation.dirty,
                })}
            >
                {vnode.attrs.label && (
                    <label>
                        {vnode.attrs.label}
                        {vnode.attrs.icon && <Icon name={vnode.attrs.icon} />}
                        {validation && <span className='validation'>{validation.label}</span>}
                    </label>
                )}
                <input
                    type='text'
                    id={this.id}
                    placeholder={vnode.attrs.placeholder ? vnode.attrs.placeholder : 'Select a date...'}
                    disabled={vnode.attrs.disabled}
                    onkeypress={() => false}
                    readonly=''
                />
                {(() => {
                    if (invalid && validation?.dirty) {
                        return <div className='help validation'>{failure_message(invalid)}</div>
                    } else if (vnode.attrs.help) {
                        return <div class='help'>{vnode.attrs.help}</div>
                    }
                    return undefined
                })()}
            </div>
        )
    }
}
